import React, { useState } from 'react';

import { dialogService } from '@thefarmersfront/kitchen';

import { type CustomCellRendererProps } from 'ag-grid-react';
import { useShallow } from 'zustand/react/shallow';

import { NumberUnitInput } from '~/components/common';

import { useReceiveStore } from '~/store';
import { formatNumber } from '~/utils';

import {
  MAX_RECEIVE_REQUEST_PRODUCT_COUNT,
  RECEIVE_MODIFICATION_TYPE,
  type TypeReceiveModificationType,
} from '~/constants';

import type {
  TypeReceiveDetailOrderItem,
  TypeReceiveItemEditableFields,
  TypeReceiveItemErrorState,
} from '~/types/receive.types';

import styles from './ReceiveRequestDetailInputCell.module.scss';

type TypeReceiveRequestDetailInputCellParams = {
  disabled: boolean;
};

type TypeReceiveRequestDetailInputCellProps = TypeReceiveRequestDetailInputCellParams &
  CustomCellRendererProps<TypeReceiveDetailOrderItem>;

/**
 * [입고관리 > 입고요청관리] 입고요청상세 모달 > Input 셀 렌더러
 */
const ReceiveRequestDetailInputCell = ({ disabled, ...params }: TypeReceiveRequestDetailInputCellProps) => {
  const {
    node: { rowIndex },
    column,
  } = params;

  const [
    detailModalOrderGoodsList,
    setDetailModalOrderGoodsList,
    detailModalOrderGoodsListStatus,
    setDetailModalOrderGoodsListStatus,
  ] = useReceiveStore(
    useShallow((state) => [
      state.detailModalOrderGoodsList,
      state.setDetailModalOrderGoodsList,
      state.detailModalOrderGoodsListStatus,
      state.setDetailModalOrderGoodsListStatus,
    ])
  );

  const colId = column?.getColId() as TypeReceiveItemEditableFields;
  const rowData = detailModalOrderGoodsList[rowIndex as number];
  const cellData = rowData?.[colId] ? String(rowData[colId]) : '';
  const isModified = rowData?.modifiedFields?.includes(colId) ?? false;

  const [shouldValidateOnBlur, setShouldValidateOnBlur] = useState(false);

  if (rowIndex === null || column === undefined || rowData === undefined) {
    return <div>-</div>;
  }

  const getUpdatedRowTotalQty = (updatedValue: number) => {
    // 총 수량 = 수량(BOX)*입수(BOX당)+수량(EA)
    let updatedRowTotalQty = 0;

    if (colId === 'boxQty') {
      updatedRowTotalQty = updatedValue * rowData.boxPerQty + rowData.eachQty;
    } else if (colId === 'boxPerQty') {
      updatedRowTotalQty = rowData.boxQty * updatedValue + rowData.eachQty;
    } else if (colId === 'eachQty') {
      updatedRowTotalQty = rowData.boxQty * rowData.boxPerQty + updatedValue;
    }

    return updatedRowTotalQty;
  };

  const getUpdatedTotalQty = (updatedValue: number) => {
    // 기존의 전체 총 수량에서, 현재 row의 총수량을 입력받은 값으로 업데이트 해 반환
    const prevTotalQty = detailModalOrderGoodsList.reduce((acc, item) => {
      return acc + item.totalQty;
    }, 0);
    const prevRowTotalQty = rowData.totalQty;

    return prevTotalQty - prevRowTotalQty + getUpdatedRowTotalQty(updatedValue);
  };

  const resetCellData = () => {
    setDetailModalOrderGoodsList([
      ...detailModalOrderGoodsList.slice(0, rowIndex),
      {
        ...rowData,
        [colId]: 0,
        totalQty: getUpdatedRowTotalQty(0),
        modificationType: RECEIVE_MODIFICATION_TYPE.MODIFY as TypeReceiveModificationType,
        modifiedFields: [...(rowData.modifiedFields ?? []), colId],
      },
      ...detailModalOrderGoodsList.slice(rowIndex + 1),
    ]);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    const numberValue = Number(value);

    if (!Number.isInteger(numberValue)) {
      return;
    }

    if (colId === 'boxPerQty' && value === '0') {
      dialogService.addDialog({
        children: '입수(BOX당)은 최소 1 이상 입력해야 합니다.',
      });
    }

    setDetailModalOrderGoodsList([
      ...detailModalOrderGoodsList.slice(0, rowIndex),
      {
        ...rowData,
        [colId]: numberValue,
        totalQty: getUpdatedRowTotalQty(numberValue),
        modificationType: RECEIVE_MODIFICATION_TYPE.MODIFY as TypeReceiveModificationType,
        modifiedFields: [...(rowData.modifiedFields ?? []), colId],
      },
      ...detailModalOrderGoodsList.slice(rowIndex + 1),
    ]);

    setDetailModalOrderGoodsListStatus(
      detailModalOrderGoodsListStatus.map((rowError, index) => {
        if (index === rowIndex) {
          return {
            ...rowError,
            ...((colId === 'boxQty' || colId === 'eachQty') && {
              boxQty: 'default',
              eachQty: 'default',
            }),
            ...(colId === 'boxPerQty' && { boxPerQty: 'default' }),
          };
        } else {
          return rowError;
        }
      })
    );

    setShouldValidateOnBlur(true);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!shouldValidateOnBlur) return;

    const value = e.target.value.trim();
    const numberValue = Number(value);

    // 수량(EA) > 입수(BOX당) 입력
    if (colId === 'eachQty' && numberValue > rowData.boxPerQty) {
      dialogService.addDialog({
        children: '수량(EA)는 입수(BOX당)보다 적은 수량만\n입력할 수 있습니다.',
        onConfirm: resetCellData,
      });
    }
    // 입수(BOX당) < 수량(EA) 입력
    else if (colId === 'boxPerQty' && numberValue > 0 && numberValue < rowData.eachQty) {
      dialogService.addDialog({
        children: '입수(BOX당)는 수량(EA)보다 큰 수량만\n입력할 수 있습니다.',
        onConfirm: resetCellData,
      });
    }
    // 총 수량 20,000 초과
    else if (getUpdatedTotalQty(numberValue) > MAX_RECEIVE_REQUEST_PRODUCT_COUNT) {
      dialogService.addDialog({
        children: `한 번에 입고 요청할 수 있는\n총 수량은 최대 ${formatNumber(
          MAX_RECEIVE_REQUEST_PRODUCT_COUNT
        )}개 입니다.`,
        onConfirm: resetCellData,
      });
    }

    setShouldValidateOnBlur(false);
  };

  return (
    <div className={styles['kitchen-component-cell']}>
      <NumberUnitInput
        className={isModified ? styles['modified'] : ''}
        size="tiny"
        placeholder="입력"
        maxLength={7}
        disabled={disabled || (colId === 'boxPerQty' && rowData.boxQty === 0)}
        status={detailModalOrderGoodsListStatus[rowIndex]?.[colId as keyof TypeReceiveItemErrorState] ?? 'default'}
        value={cellData}
        onChange={handleChange}
        onBlur={handleBlur}
      />
    </div>
  );
};

export default ReceiveRequestDetailInputCell;
